<template>
  <div id="template-editor" class="template-editor newStyle">
    <!--
    Display the currently selected template type.
    Useful for debugging.
    {{ templateObject }}
    -->

    <div v-if="!templateAssetId">
      <icon type="circle-question"/>
      {{ $t('vform.templateAssetNotFound') }}
    </div>
    <div v-else class="row">
      <div class="col-6">
        <h2>{{ $t('vform.templateEditor') }}</h2>

        <!---PREVIEW -->
        <div class="mt-2 template-preview">
          <label class="vform-label white template-label mb-2">{{ $t('Preview') }}</label><br/>
          <div class="template-preview-component" v-if="selectedTemplateType" :key="forceReRenderKey">
            <!-- Preview based on type -->
            <component
                :is="getComponentName(selectedTemplateType)"
                :key="forceReRenderKey"
                :config="templateObject"
                :state="{}"
                :template="selectedTemplate ? selectedTemplate : templateObject"
                :template-mode="true"
                lang="Unknown"
                proejct-id="no-project"></component>
          </div>
          <div v-else>
            <div v-if="selectedTemplate" class="form-error">
              <icon type="exclamation-triangle"/>
              {{ $t('vform.noTemplateSelected') }}
            </div>
          </div>
        </div>

        <div
          v-for="mode in Object.values(templateModes)"
          v-if="(mode !== templateModes.SELECT_EXISTING || selectedTemplateName)"
          :key="mode"
          class="mt-2"
          :class="['tab-selector d-inline-block', templateMode === mode ? 'active' : 'inactive']"
          @click="templateMode = mode"
        >
          {{ $t('vform.' + mode) }}
        </div>

        <div v-if="templateMode === templateModes.CREATE_NEW" class="tab-content">
          <input
              v-model="newTemplateName"
              class="form-text form-text-dark template-name"
              placeholder="New Template Name"
          />
          <div
              :class="['settings-button d-inline-block mt-0 action-button float-none mt-2', newTemplateName !== '' ? '' : 'disabled']"
               @click="newTemplateName ? createNewTemplate() : null">
            <icon size="1" type="save"/> {{ $t('vform.saveTemplate') }}
          </div>
        </div>

        <div v-if="templateMode === templateModes.SELECT_EXISTING && selectedTemplateName" class="tab-content">
          <div class="template-label">
            {{ $t('vform.selectedTemplate') }}
          </div>
          <template v-if="templates.length">
            {{ selectedTemplateName }}

            <div
                v-if="selectedTemplateKey"
                :class="['d-inline-block mt-2 ml-2 lighter', !renameMode ? 'clickable' : '']"
                @click="startRenaming">
              <icon type="pencil-alt"/>
              {{ $t('vform.renameTemplate') }}
            </div>

            <div v-if="selectedTemplateKey && !renameMode"
                 :key="forceReRenderKey"
                 class="form-error template-editor-error w-auto mt-2">
              <icon type="exclamation-triangle"/>
              {{ $t('vform.templateOverwrite') }}
            </div>

            <div v-if="renameMode" class="mt-4 ">
              <div class="template-label mb-1">
                {{ $t('vform.templateName') }}
              </div>
              <div class="d-flex">
                <input
                    :key="forceReRenderKey"
                    v-model="renameTemplateName"
                    class="form-text form-text-dark template-name"
                />
                <div class="d-inline-block ml-2 clickable"
                     @click="renameMode = false; renameTemplateName = '';">
                  <icon type="xmark"/>
                </div>
              </div>
            </div>
            <div>
              <div
                  class="settings-button d-inline-block mt-0 action-button float-none mt-2"
                  @click="updateTemplate">
                <icon size="1" type="save"/> {{ $t('vform.saveChanges') }}
              </div>
            </div>
          </template>
          <div v-else>
            {{ $t('vform.noTemplatesFound') }}
          </div>
        </div>

        <div class="for-both-tabs mt-2">
          <div v-if="unknownError"
               :key="forceReRenderKey"
               class="form-error template-editor-error w-auto">
            <icon type="exclamation-triangle"/>
            {{ unknownError }}
          </div>
          <div v-if="templateIsEmptyError" :key="forceReRenderKey" class="form-error template-editor-error">
            <icon type="exclamation-triangle"/>
            {{ $t('vform.templateIsEmpty') }}
          </div>
        </div>
      </div>
      <div class="col-6">
        <properties-panel
            id="template-editor-properties"
            :config="{steps: []}"
            :selected-element="temporaryObject"
            :show-template-applier="false"
            :state="{dragging: false}"
            :template-asset-id="templateAssetId"
            :template-editing="true"
            current-lang="Unknown"
            project-id="noproject"
        />
      </div>
    </div>
  </div>
</template>

<script>

import TEXTDISPLAY from "@/components/vForm/viewer/TEXTDISPLAY.vue";
import BUTTON_ITEM from "@/components/vForm/viewer/BUTTON_ITEM.vue";
import HEADLINE from "@/components/vForm/viewer/HEADLINE.vue";
import RADIOBUTTONS from "@/components/vForm/viewer/RADIOBUTTONS.vue";
import CHECKBOXES from "@/components/vForm/viewer/CHECKBOXES.vue";
import LOGIN from "@/components/vForm/viewer/LOGIN.vue";
import LOGOUT from "@/components/vForm/viewer/LOGOUT.vue";
import DropDown from "@/components/forms/DropDown.vue";
import {getComponentName} from "@/components/vForm/utils";
import {templateTypes, vformTemplatableFields, vFormTemplatableSubFields} from "@/enum";
import {v4 as uuidv4} from "uuid";
import {applyTemplate, getTemplateFile} from "@/components/vForm/templateFunctions";
import PropertiesPanel from "@/components/vForm/PropertiesPanel.vue";
import Icon from "@/components/Icon.vue";

export default {
  name: 'TemplateEditor',
  components: {
    PropertiesPanel,
    DropDown,
    TEXTDISPLAY,
    BUTTON_ITEM,
    HEADLINE,
    RADIOBUTTONS,
    CHECKBOXES,
    LOGIN,
    LOGOUT,
    Icon,
  },
  props: {
    object: {type: Object, required: true},
    preSelectedTemplateKey: {type: String, default: null},
    templateAssetId: {type: String, required: true}
  },
  data() {
    return {
      templateTypes,
      templateMode: 'createNew',
      templateModes: {
        CREATE_NEW: 'createNew',
        SELECT_EXISTING: 'selectExisting'
      },
      selectedTemplateType: 'Button',
      locallySelectedTemplate: {},
      newTemplateName: '',
      renameTemplateName: '',
      templates: [],
      templateObject: {},
      temporaryObject: {},
      forceReRenderKey: 0,
      unknownError: '',
      templateIsEmptyError: false,
      renameMode: false,
    };
  },
  computed: {
    selectedTemplate() {
      return this.templates ? this.templates.find(template => template.key === this.selectedTemplateKey) : null;
    },
    selectedTemplateKey() {
      return this.preSelectedTemplateKey ? this.preSelectedTemplateKey : (this.object ? this.object.templateId :  null);
    },
    selectedTemplateName() {
      return this.selectedTemplate && this.selectedTemplate.data && this.selectedTemplate.data.templateName ? this.selectedTemplate.data.templateName : '';
    },
    locallySelectedTemplateKey() {
      return this.locallySelectedTemplate && this.locallySelectedTemplate.key ? this.locallySelectedTemplate.key : null;
    }
  },
  watch: {
    temporaryObject: {
      deep: true,
      handler() {
        this.setTemplateObject();
      }
    },
    object: {
      deep: true,
      handler() {
        this.init();
      }
    },
    preSelectedTemplateKey: {
      handler() {
        this.init();
      }
    },
    selectedTemplate(val) {
      this.setLocallySelectedTemplate(val);
    },
  },
  beforeMount() {
    this.init();
  },
  mounted() {
    this.updateTemplateList();
    if(this.selectedTemplateKey) {
      this.templateMode = this.templateModes.SELECT_EXISTING;
    } else {
      this.templateMode = this.templateModes.CREATE_NEW;
    }
  },
  methods: {
    init() {
      this.selectedTemplateType = this.object.formElementType;
      this.temporaryObject = JSON.parse(JSON.stringify(this.object));
      this.setTemplateObject();
      this.setLocallySelectedTemplate(this.selectedTemplate);
    },
    startRenaming() {
      this.renameMode = true;
      this.renameTemplateName = this.locallySelectedTemplate ? this.locallySelectedTemplate.data.templateName : '';
    },
    setLocallySelectedTemplate(template) {
      this.locallySelectedTemplate = template;
      applyTemplate(this, this.temporaryObject, template);
    },
    getComponentName,
    /**
     * This is necessary because we made the stupid decision to have a global formatting which only affects buttons in the bottom drop zone
     * */
    handleBottomBarButton() {
      if (this.object.bottomDropZone) {
        if (!this.templateObject.backgroundColor) {
          this.$set(this.templateObject, 'backgroundColor', getComputedStyle(document.documentElement).getPropertyValue('--vform-editor-layout-accent-color'));
          this.$set(this.templateObject, 'color', getComputedStyle(document.documentElement).getPropertyValue('--vform-editor-emphasized-font-color'))
        }
      }
    },
    // update the name for the selected template
    renameTemplate() {
      this.renameMode = !this.renameMode;
      if (!this.renameTemplateName.trim()) {
        return;
      }
      this.selectedTemplate.data.templateName = this.renameTemplateName;
      this.renameTemplateName = '';
    },
    setTemplateObject() {
      let fields = vformTemplatableFields;
      let subFields = vFormTemplatableSubFields;
      this.templateObject = {};
      for (let i = 0; i < fields.length; i++) {
        this.$set(this.templateObject, fields[i], this.temporaryObject[fields[i]]);
      }
      for (let j = 0; j < subFields.length; j++) {
        this.setTemplateSubObjects(fields, subFields[j]);
      }
      this.handleBottomBarButton();
    },
    setTemplateSubObjects(fields, subField) {
      if (this.temporaryObject[subField]) {
        this.$set(this.templateObject, subField, {});
        for (let i = 0; i < fields.length; i++) {
          this.$set(this.templateObject[subField], fields[i], this.temporaryObject[subField][fields[i]]);
        }
      }
    },
    changeTemplate(fileName) {
      this.object.templateId = fileName;
      this.locallySelectedTemplate = this.getTemplateByFileName(fileName);
      this.deepMerge(this.temporaryObject, this.locallySelectedTemplate.data);
    },
    getTemplateByFileName(fileName) {
      return this.templates.find(template => template.key === fileName);
    },
    // Shallow copies don't work since null values need to be overwritten
    isObject(obj) {
      return obj && typeof obj === 'object' && !Array.isArray(obj);
    },
    deepMerge(target, source) {
      for (let key in source) {
        if (this.isObject(source[key])) {
          if (!target[key]) {
            target[key] = {};
          }
          this.deepMerge(target[key], source[key]);
        } else {
          // Directly assign the value, allowing null to overwrite target's values
          target[key] = source[key];
        }
      }
    },
    async updateTemplateList() {
      let files = await getTemplateFile(this.$store, this.templateAssetId, {
        metaFilter: {
          templateType: templateTypes.FORM_BLOCK
        }
      });
      files = files.filter(item => {
        return item.data && item.data.formElementType === this.object.formElementType;
      })
      this.templates = files ? files : [];
    },
    async saveTemplateFile(args) {
      const {existingId, name} = args;
      const fileName = existingId ? existingId : (uuidv4() + '.json');
      this.templateObject.templateName = name;
      await this.$store.dispatch('createAssetTextFile', {
        id: this.templateAssetId,
        fileName,
        fileContent: JSON.stringify(this.templateObject)
      }).then(async () => {
        const attributes = {
          templateType: templateTypes.FORM_BLOCK,
          itemType: this.templateObject.formElementType,
          templateName: this.templateObject.templateName,
          templateVersion: '1.0'
        };
        await this.$store.dispatch('clientUpdateAssetMeta', {
          id: this.templateAssetId,
          key: fileName,
          meta: {attributes}
        })

        // Intentionally add a timeout to not trigger the "no templates found" error under templateApplier.vue (Refer to line 4 - 7)
        setTimeout(() => {
          applyTemplate(this, this.object, {
            data: this.templateObject,
            key: fileName
          });
        }, 200)

        this.$emit('templateSaved');
        this.$emit('templateFileChanged');
      }).catch(e => {
        console.log(e);
        this.unknownError = "There has been an error saving the template.";
        this.forceReRenderKey++;
      })
    },
    async createNewTemplate() {
      const existingTemplate = this.templates ? this.templates.find(t => t.templateName === this.newTemplateName.trim()) : null;
      if (existingTemplate) {
        this.templateIsEmptyError = true;
        return;
      }
      const name = this.newTemplateName.trim() ? this.newTemplateName.trim() : 'Unnamed Template';
      await this.saveTemplateFile({name});
    },
    async updateTemplate() {
      this.renameTemplate();
      const existingName = this.selectedTemplate.data.templateName;
      await this.saveTemplateFile({
        existingId: this.locallySelectedTemplateKey,
        name: this.renameTemplateName.trim() ? this.renameTemplateName.trim() : existingName
      });
    },
  },
};
</script>

<style lang="scss">
#template-editor-properties .form-elements-container {
  font-size: 16px;
}
</style>

<style lang="scss" scoped>
.template-name {
  max-width: 300px;
  width: 100%;
}

.template-label {
  max-width: 300px;
  opacity: 0.5;
}

.template-preview {
  max-width: 300px;
  color: black;
  background-color: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.15);
  padding: 15px;
  border-radius: 2px;
  .template-preview-component {
    padding: 15px 15px;
  }
  > div {
    font-size: 16px;
  }
}

.newStyle {
  .template-label {
    font-size: 14px !important;
  }
}

.template-editor {
  font-size: 14px;
  height: auto;


  .vform-header h1.with-bg,
  .vform-header h2.with-bg,
  .vform-header h3.with-bg {
    width: auto;
    margin-left: 0;
  }
}

.template-editor-error {
  font-size: 14px;
}

.tab-selector {
  padding: 2px 4px;
  border-radius: 3px;
  background-color: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.15);
  border-bottom: 0;
  margin-bottom: -1px;
  margin-right: 4px;
  opacity: 0.4;
  transition: all 300ms ease;
  cursor: pointer;
  &.active, &:hover {
    opacity: 1;
  }
}
.tab-content {
  background-color: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.15);
  padding: 15px;
}

</style>
